import React from 'react';
import { Button, Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup } from '@chakra-ui/react';
import { LuColumns } from 'react-icons/lu';

export type PaymentLinksColumnKey = 'concept' | 'amount' | 'status' | 'createdAt' | 'expireDate' | 'link';

export const PAYMENT_LINKS_COLUMNS: { key: PaymentLinksColumnKey; label: string }[] = [
  { key: 'concept', label: 'Concepto' },
  { key: 'amount', label: 'Monto' },
  { key: 'status', label: 'Estado' },
  { key: 'createdAt', label: 'Fecha de creación' },
  { key: 'expireDate', label: 'Fecha de expiración' },
  { key: 'link', label: 'Link' },
];

export type PaymentLinksVisibleColumns = PaymentLinksColumnKey[];

export const defaultVisibleColumns = (): PaymentLinksVisibleColumns => PAYMENT_LINKS_COLUMNS.map((column) => column.key);

interface ColumnsMenuProps {
  visibleColumns: PaymentLinksVisibleColumns;
  onChange: (columns: PaymentLinksVisibleColumns) => void;
}

/**
 * Menú "Columnas" para mostrar/ocultar columnas de la tabla de Consulta de Links de
 * Pago. Usa `MenuOptionGroup`/`MenuItemOption` (el patrón nativo de Chakra para menús
 * con checkboxes) en vez de anidar un `Checkbox` dentro de un `MenuItem`: un
 * `MenuItem` renderiza un `<button>`, y un `<button>` no puede contener contenido
 * interactivo (un `<label><input>`) sin romper el árbol de accesibilidad. Solo
 * controla qué se renderiza en `PaymentLinksTable`; no afecta la consulta al BFF ni
 * la paginación.
 *
 * El label/ícono del botón y de cada `MenuItemOption` se renderizan dentro de un
 * `<span>` sin clase propia, así que heredan el estilo global `span { color:
 * primary.200 }` de theme.ts (casi blanco) — de ahí que se vieran "apagados" pese a
 * `colorScheme="button-primary"` (ese colorScheme sí tiñe el botón/borde, pero no
 * alcanza al span hijo). Se sobreescribe con `css` en ambos, sin tocar la regla
 * global.
 */
export const ColumnsMenu = ({ visibleColumns, onChange }: ColumnsMenuProps) => (
  <Menu closeOnSelect={false}>
    <MenuButton
      as={Button}
      leftIcon={<LuColumns />}
      variant="outline"
      colorScheme="button-primary"
      size="sm"
      css={{ span: { color: 'var(--chakra-colors-button-primary-500)' } }}
    >
      Columnas
    </MenuButton>
    <MenuList css={{ span: { color: 'var(--chakra-colors-neutral-800)' } }}>
      <MenuOptionGroup type="checkbox" value={visibleColumns} onChange={(value) => onChange(value as PaymentLinksVisibleColumns)}>
        {PAYMENT_LINKS_COLUMNS.map((column) => (
          <MenuItemOption key={column.key} value={column.key}>
            {column.label}
          </MenuItemOption>
        ))}
      </MenuOptionGroup>
    </MenuList>
  </Menu>
);
